import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  Badge,
  VStack,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface ChangelogEntry {
  version: string;
  date: string;
  changes: { type: 'fix' | 'feature' | 'improvement'; textKey: string }[];
}

const CHANGELOG: ChangelogEntry[] = [
  {
    version: '1.1.6',
    date: '2026-04-03',
    changes: [
      { type: 'feature', textKey: 'changelog.v116.colorclash' },
      { type: 'feature', textKey: 'changelog.v116.colorclashFeatures' },
      { type: 'feature', textKey: 'changelog.v116.easterCategory' },
      { type: 'feature', textKey: 'changelog.v116.easterFeedback' },
      { type: 'improvement', textKey: 'changelog.v116.lobbyRedesign' },
      { type: 'improvement', textKey: 'changelog.v116.postLoginRedirect' },
      { type: 'fix', textKey: 'changelog.v116.translationFix' },
    ],
  },
  {
    version: '1.1.5',
    date: '2026-03-31',
    changes: [
      { type: 'feature', textKey: 'changelog.v115.translationSystem' },
      { type: 'feature', textKey: 'changelog.v115.voToggle' },
      { type: 'feature', textKey: 'changelog.v115.fullI18n' },
      { type: 'feature', textKey: 'changelog.v115.feedbackSystem' },
      { type: 'feature', textKey: 'changelog.v115.adminDashboard' },
      { type: 'feature', textKey: 'changelog.v115.adminImportExport' },
      { type: 'feature', textKey: 'changelog.v115.top3Daily' },
      { type: 'feature', textKey: 'changelog.v115.top3Weekly' },
      { type: 'feature', textKey: 'changelog.v115.easterHunt' },
      { type: 'feature', textKey: 'changelog.v115.accountDeletion' },
      { type: 'feature', textKey: 'changelog.v115.usersTable' },
      { type: 'feature', textKey: 'changelog.v115.aprilFoolsSurprise' },
      { type: 'improvement', textKey: 'changelog.v115.categoryAllLangs' },
      { type: 'improvement', textKey: 'changelog.v115.randomPseudo' },
      { type: 'fix', textKey: 'changelog.v115.weeklyLeaderboardFix' },
    ],
  },
  {
    version: '1.1.4',
    date: '2026-03-25',
    changes: [
      { type: 'feature', textKey: 'changelog.v114.easterTheme' },
      { type: 'fix', textKey: 'changelog.v114.antiCheatDefault' },
      { type: 'fix', textKey: 'changelog.v114.antiCheatPostAnswer' },
      { type: 'fix', textKey: 'changelog.v114.weeklySubstitutionFull' },
      { type: 'fix', textKey: 'changelog.v114.weeklySubstitutionOwn' },
      { type: 'fix', textKey: 'changelog.v114.syncduelRedirect' },
      { type: 'improvement', textKey: 'changelog.v114.svgImages' },
    ],
  },
  {
    version: '1.1.3',
    date: '2026-03-22',
    changes: [
      { type: 'feature', textKey: 'changelog.v113.smartclashRename' },
      { type: 'feature', textKey: 'changelog.v113.smartclashStats' },
      { type: 'feature', textKey: 'changelog.v113.xpDailyStreak' },
      { type: 'feature', textKey: 'changelog.v113.xpWeekly' },
      { type: 'feature', textKey: 'changelog.v113.xpSmartclash' },
      { type: 'feature', textKey: 'changelog.v113.statsRedesign' },
      { type: 'feature', textKey: 'changelog.v113.goofyComments' },
      { type: 'feature', textKey: 'changelog.v113.categoryEnhanced' },
      { type: 'feature', textKey: 'changelog.v113.weeklyStats' },
      { type: 'feature', textKey: 'changelog.v113.weeklyXpBonus' },
      { type: 'feature', textKey: 'changelog.v113.weeklyCreatorSub' },
      { type: 'improvement', textKey: 'changelog.v113.weeklyMinQuestions' },
      { type: 'feature', textKey: 'changelog.v113.multiLobby' },
      { type: 'feature', textKey: 'changelog.v113.categorySortCount' },
      { type: 'feature', textKey: 'changelog.v113.tutorialRewrite' },
      { type: 'improvement', textKey: 'changelog.v113.mobileFirst' },
      { type: 'improvement', textKey: 'changelog.v113.accordionLayout' },
      { type: 'improvement', textKey: 'changelog.v113.legalUpdate' },
      { type: 'fix', textKey: 'changelog.v113.privateCategories' },
      { type: 'fix', textKey: 'changelog.v113.syncduelMobile' },
    ],
  },
  {
    version: '1.1.2',
    date: '2026-03-05',
    changes: [
      { type: 'fix', textKey: 'changelog.v112.blockedPenalty' },
      { type: 'fix', textKey: 'changelog.v112.questionDedup' },
      { type: 'fix', textKey: 'changelog.v112.questionDedupMulti' },
      { type: 'improvement', textKey: 'changelog.v112.addPlayerButton' },
      { type: 'improvement', textKey: 'changelog.v112.footerUpdate' },
    ],
  },
];

const typeBadge = (type: 'fix' | 'feature' | 'improvement') => {
  switch (type) {
    case 'fix':
      return <Badge colorScheme="red" fontSize="0.65em" px={1.5} borderRadius="sm">FIX</Badge>;
    case 'feature':
      return <Badge colorScheme="green" fontSize="0.65em" px={1.5} borderRadius="sm">NEW</Badge>;
    case 'improvement':
      return <Badge colorScheme="blue" fontSize="0.65em" px={1.5} borderRadius="sm">IMPROVE</Badge>;
  }
};

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangelogModal: React.FC<ChangelogModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay bg="rgba(0,0,0,0.6)" backdropFilter="blur(4px)" />
      <ModalContent
        bg="var(--gris-bleute)"
        border="1px solid rgba(139, 148, 179, 0.2)"
        borderRadius="xl"
        maxH="80vh"
      >
        <ModalHeader color="var(--texte-clair)" fontSize="lg" pb={2}>
          {t('changelog.title')}
        </ModalHeader>
        <ModalCloseButton color="var(--texte-secondaire)" />
        <ModalBody pb={6}>
          <VStack spacing={5} align="stretch">
            {CHANGELOG.map((entry, idx) => (
              <Box key={entry.version}>
                {idx > 0 && <Divider borderColor="rgba(139, 148, 179, 0.15)" mb={4} />}
                <HStack mb={2} align="baseline">
                  <Text color="var(--bleu-electrique)" fontWeight="bold" fontSize="md">
                    v{entry.version}
                  </Text>
                  <Text color="var(--texte-secondaire)" fontSize="xs">
                    {entry.date}
                  </Text>
                  {idx === 0 && (
                    <Badge colorScheme="purple" fontSize="0.6em" ml={1}>
                      {t('changelog.latest')}
                    </Badge>
                  )}
                </HStack>
                <VStack spacing={1.5} align="stretch" pl={2}>
                  {entry.changes.map((change, i) => (
                    <HStack key={i} spacing={2} align="start">
                      {typeBadge(change.type)}
                      <Text color="var(--texte-clair)" fontSize="sm" lineHeight="short">
                        {t(change.textKey)}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ChangelogModal;
